import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Package, Truck, Users, Activity, ArrowRight, MailQuestion } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { UseAdminDashboardReturn } from '../../hooks/useAdminDashboard';
import type { PendingContactRequest } from '../../types/dashboard';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { Button } from "../ui/button";
import { Skeleton } from "../ui/skeleton";

export const AdminDashboard: React.FC<UseAdminDashboardReturn> = ({
  stats,
  loading,
  error,
  isRecentActivityFetching,
  recentActivityPage,
  setRecentActivityPage,
  isMonthModalOpen,
  setIsMonthModalOpen,
  openMonthModal,
  goToCompanies,
  goToClients,
  goToPendingQuotes,
  goToQuote,
}) => {
  const { t, i18n } = useTranslation();

  if (loading) {
    return (
      <div className="space-y-6">
        <h2 className="text-3xl tracking-tight">{t('admin.dashboard.title')}</h2>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i}>
              <CardHeader className="pb-2">
                <Skeleton className="h-4 w-[100px]" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-8 w-[60px]" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (error || !stats) {
    return <div className="text-destructive">{error || t('admin.dashboard.error_loading_data')}</div>;
  }

  // Server-side pagination for the recent-activity feed. The pagination block
  // arrives alongside the current page of activities; fall back to sane
  // defaults so a single-page feed (or an older payload) still renders.
  const recentActivitiesPagination = stats.recent_activities_pagination;
  const currentActivityPage = recentActivitiesPagination?.current_page ?? recentActivityPage;
  const totalActivityPages = recentActivitiesPagination?.total_pages ?? 1;

  // Controls are disabled while a page is loading (prevents overlapping
  // requests) and at the first/last page boundaries.
  const canGoToPreviousPage = !isRecentActivityFetching && currentActivityPage > 1;
  const canGoToNextPage = !isRecentActivityFetching && currentActivityPage < totalActivityPages;

  const goToPreviousActivityPage = () =>
    setRecentActivityPage(Math.max(1, currentActivityPage - 1));
  const goToNextActivityPage = () =>
    setRecentActivityPage(Math.min(totalActivityPages, currentActivityPage + 1));

  const formatRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    
    if (diffInSeconds < 60) return t('admin.dashboard.time.just_now');
    if (diffInSeconds < 3600) {
      return t('admin.dashboard.time.minutes_ago', {
        count: Math.floor(diffInSeconds / 60)
      });
    }
    if (diffInSeconds < 86400) {
      return t('admin.dashboard.time.hours_ago', {
        count: Math.floor(diffInSeconds / 3600)
      });
    }
    if (diffInSeconds < 172800) return t('admin.dashboard.time.yesterday');
    return date.toLocaleDateString();
  };

  const formatMonthLabel = (monthValue: string) => {
    const [year, month] = monthValue.split('-').map(Number);
    if (!year || !month) return monthValue;

    const date = new Date(year, month - 1, 1);
    if (Number.isNaN(date.getTime())) return monthValue;

    return new Intl.DateTimeFormat(i18n.language, {
      month: 'long',
      year: 'numeric'
    }).format(date);
  };

  // Normalize the raw `auditable_type` (e.g. "TransportationCompany") into a
  // short slug so we never display Rails class names in the UI. Combines it
  // with the action to pick a translation key. Unknown combinations — and
  // known types paired with an action we have no translation for — fall back
  // to a generic localized message.
  const AUDITABLE_SLUGS: Record<string, string> = {
    TransportationCompany: 'carrier',
    CargoQuoteRequest: 'quote',
    QuoteResponse: 'response',
    User: 'user',
  };

  // Whitelist of (slug, action) pairs that have a translation key. Any pair
  // not listed here is treated as unsupported and falls back to the generic
  // message — this prevents raw translation keys (e.g. carrier_reactivate)
  // from leaking into the UI when the backend logs an action we haven't
  // translated for that type.
  const SUPPORTED_ACTIONS: Record<string, Set<string>> = {
    carrier: new Set(['create', 'update', 'delete']),
    quote: new Set(['update', 'delete']),
    response: new Set(['create', 'update', 'delete']),
    user: new Set(['create', 'update', 'delete', 'deactivate', 'reactivate']),
  };

  const formatActivityMessage = (action: string, auditableType: string): string => {
    const slug = AUDITABLE_SLUGS[auditableType];
    if (slug && SUPPORTED_ACTIONS[slug]?.has(action)) {
      return `admin.dashboard.activity.${slug}_${action}`;
    }
    return 'admin.dashboard.activity.fallback';
  };

  return (
    <div className="space-y-6">
      <h2 className="text-3xl tracking-tight">{t('admin.dashboard.title')}</h2>
      
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {/* Monthly Quotes */}
        <Card 
          className="cursor-pointer hover:bg-accent transition-colors"
          onClick={openMonthModal}
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('admin.dashboard.monthly_quotes')}</CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl">{stats.monthly_quotes.current_month}</div>
            <p className={`text-xs ${stats.monthly_quotes.change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {stats.monthly_quotes.change >= 0 ? '+' : ''}{stats.monthly_quotes.change} {t('admin.dashboard.from_last_month')}
            </p>
          </CardContent>
        </Card>
        
        {/* Active Companies */}
        <Card 
          className="cursor-pointer hover:bg-accent transition-colors"
          onClick={goToCompanies}
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('admin.dashboard.active_companies')}</CardTitle>
            <Truck className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl">{stats.active_companies_count}</div>
          </CardContent>
        </Card>
        
        {/* Clients */}
        <Card 
          className="cursor-pointer hover:bg-accent transition-colors"
          onClick={goToClients}
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('admin.dashboard.clients')}</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl">{stats.clients_count}</div>
          </CardContent>
        </Card>
        
        {/* Pending Responses */}
        <Card 
          className="cursor-pointer hover:bg-accent transition-colors"
          onClick={goToPendingQuotes}
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('admin.dashboard.pending_responses')}</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl">{stats.pending_responses_count}</div>
            <p className="text-xs text-muted-foreground">
              {t('admin.dashboard.requiring_attention')}
            </p>
          </CardContent>
        </Card>
      </div>

      <Card data-testid="pending-contact-requests-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MailQuestion className="h-4 w-4 text-muted-foreground" />
            {t('admin.dashboard.pending_contact_requests.title')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {stats.pending_contact_requests.length === 0 ? (
            <div className="text-sm text-muted-foreground">
              {t('admin.dashboard.pending_contact_requests.empty')}
            </div>
          ) : (
            <ul className="space-y-3" data-testid="pending-contact-requests-list">
              {stats.pending_contact_requests.map((request) => (
                <PendingContactRequestRow
                  key={request.id}
                  request={request}
                  formatRelativeTime={formatRelativeTime}
                  onSelect={goToQuote}
                  viewLabel={t('admin.dashboard.pending_contact_requests.view_quote')}
                  carrierCountLabel={(count) =>
                    count === 1
                      ? t('admin.dashboard.pending_contact_requests.carrier_count_one', { count })
                      : t('admin.dashboard.pending_contact_requests.carrier_count_other', { count })
                  }
                  requestedByLabel={(name) =>
                    t('admin.dashboard.pending_contact_requests.requested_by', { name })
                  }
                />
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <div className="grid gap-4 md:grid-cols-1">
        <Card>
          <CardHeader>
            <CardTitle>{t('admin.dashboard.recent_activity')}</CardTitle>
          </CardHeader>
          <CardContent>
            {stats.recent_activities.length === 0 ? (
              <div className="text-sm text-muted-foreground">
                {t('admin.dashboard.no_recent_activity')}
              </div>
            ) : (
              <div className="space-y-4">
                {stats.recent_activities.map((activity) => (
                  <div key={activity.id} className="flex items-center justify-between border-b pb-2 last:border-0 last:pb-0">
                    <div className="flex flex-col">
                      <span className="font-medium">{activity.user_name}</span>
                      <span className="text-sm text-muted-foreground">
                        {t(formatActivityMessage(activity.action, activity.auditable_type))}
                      </span>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {formatRelativeTime(activity.created_at)}
                    </span>
                  </div>
                ))}
              </div>
            )}

            <div
              className="flex items-center justify-between gap-3 mt-4 pt-4 border-t"
              data-testid="recent-activity-pagination"
            >
              <span
                className="text-sm text-muted-foreground"
                aria-live="polite"
                data-testid="recent-activity-page-info"
              >
                {t('common.page_info', {
                  current: currentActivityPage,
                  total: totalActivityPages || 1,
                })}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={goToPreviousActivityPage}
                  disabled={!canGoToPreviousPage}
                >
                  {t('common.previous')}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={goToNextActivityPage}
                  disabled={!canGoToNextPage}
                >
                  {t('common.next')}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Dialog open={isMonthModalOpen} onOpenChange={setIsMonthModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('admin.dashboard.quotes_by_month_title')}</DialogTitle>
          </DialogHeader>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('admin.dashboard.month')}</TableHead>
                <TableHead>{t('admin.dashboard.count')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {stats.quotes_by_month.map((item) => (
                <TableRow key={item.month}>
                  <TableCell>{formatMonthLabel(item.month)}</TableCell>
                  <TableCell>{item.count}</TableCell>
                </TableRow>
              ))}
              {stats.quotes_by_month.length === 0 && (
                <TableRow>
                  <TableCell colSpan={2} className="text-center text-muted-foreground">
                    {t('admin.dashboard.no_quotes_by_month')}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <div className="flex justify-end">
            <Button variant="secondary" onClick={() => setIsMonthModalOpen(false)}>
              {t('admin.dashboard.close')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

interface PendingContactRequestRowProps {
  request: PendingContactRequest;
  formatRelativeTime: (dateString: string) => string;
  onSelect: (quoteId: number) => void;
  viewLabel: string;
  carrierCountLabel: (count: number) => string;
  requestedByLabel: (name: string) => string;
}

const PendingContactRequestRow: React.FC<PendingContactRequestRowProps> = ({
  request,
  formatRelativeTime,
  onSelect,
  viewLabel,
  carrierCountLabel,
  requestedByLabel,
}) => {
  return (
    <li
      data-testid={`pending-contact-request-${request.id}`}
      className="flex items-center justify-between border rounded-lg p-3 hover:bg-accent/40 transition-colors"
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium truncate" data-testid="pending-contact-request-tracking">
            {request.tracking_number}
          </span>
          <span className="text-sm text-muted-foreground">
            {request.origin_country} → {request.destination_country}
          </span>
        </div>
        <div className="text-sm text-muted-foreground mt-1 flex items-center gap-3 flex-wrap">
          <span data-testid="pending-contact-request-count">
            {carrierCountLabel(request.pending_response_count)}
          </span>
          {request.contact_name && (
            <span>{requestedByLabel(request.contact_name)}</span>
          )}
          {request.last_requested_at && (
            <span>{formatRelativeTime(request.last_requested_at)}</span>
          )}
        </div>
      </div>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => onSelect(request.id)}
        data-testid={`pending-contact-request-view-${request.id}`}
      >
        {viewLabel}
        <ArrowRight className="h-4 w-4 ml-1" />
      </Button>
    </li>
  );
};
